/**
 * Offline FAQ matcher — Phase 6 sub-commit 6a (13 Jun 2026).
 *
 * Pure function. Given a guest's natural-language chat query and the
 * partner's FAQ catalogue (fetched via src/lib/api/faqs.ts and pre-
 * cached on first signed-in mount via src/lib/api/prefetch.ts), pick
 * the FAQ whose `question` field best matches the query — IF the
 * match clears a confidence threshold. Otherwise return null.
 *
 * Consumed by MePage's handleSend in sub-commit 6b: when shouldSend
 * is false, the matcher runs against the cached FAQs and a successful
 * match drives a `kind: 'persona-offline-faq'` bubble instead of
 * queueing the message to the outbox. A null result falls through to
 * the normal queue-for-later behaviour.
 *
 * --------------------------------------------------------------------
 * Threshold posture — false-negative over false-positive
 * --------------------------------------------------------------------
 *
 * The match score is `overlap / max(query.size, candidate.size)` —
 * a Jaccard-ish ratio anchored to the larger token set so verbose
 * queries against terse FAQs (and vice versa) can't game the score
 * upward. The threshold is set to 0.5, deliberately conservative.
 *
 * Premium discipline: a "Saved info" bubble that confidently delivers
 * a wrong answer is worse than no bubble at all. Better to fall through
 * to "we'll answer when you're back online" than to misattribute a
 * confident answer to the persona. A guest can always re-ask online
 * for the live LLM reply; a guest who's been confidently mis-answered
 * may lose trust in the channel entirely.
 *
 * --------------------------------------------------------------------
 * Tokenization
 * --------------------------------------------------------------------
 *
 *   1. lowercase + strip hyphens ("wi-fi" → "wifi")
 *   2. split on non-alphanumeric runs
 *   3. drop tokens shorter than 2 chars
 *   4. drop stopwords (function/filler words that don't discriminate)
 *   5. apply naive trailing-s stem ("dogs" → "dog", "opens" → "open")
 *      — skips "ss" endings so "address" doesn't lose its terminal s.
 *      Doesn't handle -es / -ies / -ing / etc.; those are rare in
 *      partner-FAQ language and the cost of mismatch is "fall back
 *      to no-saved-info", which is acceptable.
 *   6. expand small alias map for common UK caravan-park vocabulary
 *      (wifi↔internet, pool↔swimming, loo↔toilet, brekkie↔breakfast,
 *      pub↔bar) PLUS compound-word decompositions (checkout →
 *      check+out, checkin → check+in) so split-form queries match
 *      compound-form FAQs and vice versa.
 *
 * No external dependencies. ~80 lines, deterministic, easy to unit-
 * test with fixture FAQs. Considered fuse.js but the bundle cost
 * isn't justified at our scale (typical partner has <50 FAQs;
 * token-overlap suffices) and the threshold tuning is more
 * predictable when we own the algorithm.
 *
 * Field scope: only `faq.question` is matched against the query.
 * Matching the answer too was considered (would catch questions
 * phrased differently from the canonical FAQ but containing terms
 * from the answer). Deferred: answer text tends to be verbose and
 * full of incidental tokens; matching against it inflates the
 * false-positive risk. Revisit empirically if real-world signal
 * shows under-matching.
 */
import type { GuestFaq } from './api/faqs';
/**
 * Minimum overlap ratio to declare a match. Token-overlap ratio is
 * `overlap / max(query.size, candidate.size)`. At 0.5:
 *   - "breakfast time?" (2 tokens) vs "What time is breakfast?"
 *     (2 tokens after stopword removal) → 2/2 = 1.0, match.
 *   - "wifi" (1 token) vs "Is there wifi?" (1 token after stopword
 *     removal) → 1/1 = 1.0, match.
 *   - "weather today?" (2 tokens) vs "What time is breakfast?"
 *     (2 tokens) → 0/2 = 0.0, no match.
 *   - "can my dog come?" (2 tokens after stopword + stem) vs
 *     "Are dogs allowed?" (2 tokens) → 1/2 = 0.5, match (at threshold).
 *
 * Tune empirically on real partner FAQ corpora after launch. Tests
 * lock current behaviour; raising the threshold tightens matches and
 * will fail tests in lockstep.
 */
const MATCH_THRESHOLD = 0.5;
/**
 * Stopwords — high-frequency function words that rarely discriminate
 * between FAQ questions. Includes pronouns, articles, common verbs,
 * wh-words, and politeness fillers. Hand-curated for UK English; not
 * sourced from a generic NLTK list because we want to keep verbs
 * like "open", "close", "swim", "eat" — they're discriminating in
 * the FAQ domain.
 */
const STOPWORDS = new Set<string>([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'do',
  'does',
  'for',
  'from',
  'has',
  'have',
  'how',
  'i',
  'if',
  'in',
  'is',
  'it',
  'its',
  'just',
  'me',
  'my',
  'of',
  'on',
  'or',
  'our',
  'so',
  'than',
  'that',
  'the',
  'their',
  'theirs',
  'them',
  'then',
  'there',
  'these',
  'they',
  'this',
  'those',
  'to',
  'too',
  'we',
  'what',
  'when',
  'where',
  'which',
  'who',
  'whom',
  'why',
  'will',
  'with',
  'you',
  'your',
  'yours',
  // Politeness / conversational fillers
  'please',
  'thanks',
  'thank',
  'hi',
  'hello',
  'hey',
  // Common high-frequency verbs that don't discriminate FAQ topics
  'can',
  'get',
  'got',
  'go',
  'going',
  'know',
  'see',
  'tell',
  'need',
  'want',
  'help',
]);
/**
 * Alias map — two categories of entries, same expansion mechanism.
 *
 * (1) Synonym pairs — symmetric. Each key's value list is reciprocated
 * by the corresponding value's key. When a token matches a key here,
 * its alias values are also added to the token set, so a query saying
 * "wifi" matches an FAQ saying "internet" and vice versa.
 *
 * (2) Compound-word decompositions — asymmetric. When a compound form
 * appears (e.g. "checkout"), the parts ("check", "out") are also added
 * to the token set. This bridges the joined/split gap so a guest who
 * types "check out" (two words) matches an FAQ written as "check-out"
 * (hyphenated → stripped to "checkout") or "checkout" (single word).
 * Reverse direction works naturally: query "checkout" expands to
 * {checkout, check, out} which overlaps with FAQ split-form
 * tokens {check, out}.
 *
 * Both kept deliberately small — over-aliasing inflates token-set
 * sizes and dilutes the overlap ratio. Extend empirically based on
 * real partner FAQ language. Note compound entries WILL bypass the
 * stopword filter for their parts (e.g. "in" gets added via the
 * checkin alias despite being a stopword); this is intentional —
 * structural tokens added via aliasing are part of the compound's
 * meaning, not filler.
 */
const ALIASES: Record<string, readonly string[]> = {
  // Synonym pairs (symmetric)
  wifi: ['internet'],
  internet: ['wifi'],
  pool: ['swimming'],
  swimming: ['pool'],
  brekkie: ['breakfast'],
  breakfast: ['brekkie'],
  loo: ['toilet'],
  toilet: ['loo'],
  pub: ['bar'],
  bar: ['pub'],
  // Compound-word decompositions (asymmetric — compound → parts)
  checkout: ['check', 'out'],
  checkin: ['check', 'in'],
};
/**
 * Naive trailing-s stemmer. Strips one trailing 's' from tokens of
 * length >= 4 that don't end in 'ss'. Catches plurals (dogs → dog,
 * cats → cat) and -s verb forms (opens → open). Doesn't handle -es
 * (boxes → box), -ies (puppies → puppy), or -ing (running → run);
 * those are uncommon enough in FAQ language that the cost of a missed
 * match (fall through to no-saved-info, which is acceptable) doesn't
 * justify the algorithm's complexity.
 *
 * The >= 4 guard prevents stripping legitimate short words like "us"
 * → "u" and "is" → "i" (also caught by stopwords, but defence in
 * depth). The !endsWith('ss') guard preserves "address", "process".
 */
function stem(token: string): string {
  if (token.length >= 4 && token.endsWith('s') && !token.endsWith('ss')) {
    return token.slice(0, -1);
  }
  return token;
}
function tokenize(input: string): Set<string> {
  const normalized = input.toLowerCase().replace(/-/g, '');
  const out = new Set<string>();
  for (const raw of normalized.split(/[^a-z0-9]+/)) {
    if (raw.length < 2) continue;
    if (STOPWORDS.has(raw)) continue;
    const stemmed = stem(raw);
    out.add(stemmed);
    for (const alias of ALIASES[stemmed] ?? []) {
      out.add(alias);
    }
  }
  return out;
}
export interface FaqMatch {
  faq: GuestFaq;
  score: number;
}
/**
 * Match the query against the FAQ catalogue. Returns the best-scoring
 * FAQ above MATCH_THRESHOLD, or null if no candidate clears the bar.
 *
 * Stable ordering: ties broken by insertion order (i.e. the first FAQ
 * to reach the current best score wins). FAQ arrays from useFaqs() are
 * already ordered by displayOrder + question (server-side), so partner-
 * configured priority is honoured naturally on ties.
 *
 * Returns null for:
 *   - empty query string
 *   - all-stopword query
 *   - empty FAQ list
 *   - no FAQ reaching the threshold
 *
 * Complexity: O(N × max_tokens) where N is the number of FAQs. At our
 * scale (N < 50, tokens < 20 per question) this is trivial — no
 * indexing layer needed.
 */
export function matchFaq(
  query: string,
  faqs: readonly GuestFaq[],
): FaqMatch | null {
  const queryTokens = tokenize(query);
  if (queryTokens.size === 0) return null;
  if (faqs.length === 0) return null;
  let best: FaqMatch | null = null;
  for (const faq of faqs) {
    const faqTokens = tokenize(faq.question);
    if (faqTokens.size === 0) continue;
    let overlap = 0;
    for (const token of queryTokens) {
      if (faqTokens.has(token)) overlap++;
    }
    const score = overlap / Math.max(queryTokens.size, faqTokens.size);
    if (score >= MATCH_THRESHOLD && (best === null || score > best.score)) {
      best = { faq, score };
    }
  }
  return best;
}
